/**
 * © 2025 Grupo Salamander SAPI de CV
 *
 * Input component overrides for the Renaissance design system.
 * Covers MuiFilledInput, MuiPickersFilledInput, MuiOutlinedInput,
 * MuiInputLabel, MuiFormHelperText, MuiFormLabel, MuiSelect.
 *
 * The filled/outlined/label rules are the floating-label variant approved on
 * the redesign canvas 2026-09-18 (spec, "Form fields"), ported by plan task
 * 1.1: no Material tinted fill, no underline, a hairline border, the single
 * easing curve, and a quiet label. The ring is drawn on `:has(:focus-visible)`
 * rather than `.Mui-focused` — task 1.2 generalises the same `focusRing()`
 * (now `../focusRing`) to every interactive role; re-exported here for
 * call sites that already import it from this file.
 */

import type { TPaletteDefinition } from '../palette'
import { focusRing } from '../focusRing'
import {
  density,
  radius,
  sizing,
  space,
  surface,
  target,
  transitionFor,
  type TDensity,
} from '../tokens'

export { focusRing } from '../focusRing'

// NOTE: no `input` padding override on the filled variant, deliberately. MUI's
// filled padding is asymmetric to fit the shrunk label above the value;
// flattening it makes the label collide with the value.
function filledInputRoot(
  palette: TPaletteDefinition,
  mode: 'light' | 'dark',
  densityKey: TDensity,
  reducedMotion: boolean
) {
  return {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderStyle: 'solid' as const,
    borderColor: surface[mode].separator,
    borderRadius: radius.row,
    minHeight: density[densityKey].controlHeight,
    transition: transitionFor(
      'feedback',
      ['border-color', 'box-shadow'],
      reducedMotion
    ),
    '&:hover': { backgroundColor: 'transparent' },
    '&.Mui-focused': { backgroundColor: 'transparent' },
    '&.Mui-disabled': { backgroundColor: 'transparent' },
    '&.Mui-error': { borderColor: palette.error.main },
    '@media (pointer: coarse)': { minHeight: target.coarseMin },
    '&:has(:focus-visible)': focusRing(palette, mode),
  }
}

export function inputOverrides(
  palette: TPaletteDefinition,
  mode: 'light' | 'dark' = 'light',
  densityKey: TDensity = 'regular',
  reducedMotion = false
) {
  const d = density[densityKey]
  return {
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: palette.gray.main,
          '&.Mui-focused': {
            color: palette.gray.main,
          },
          '&.Mui-error': {
            color: palette.error.main,
          },
        },
      },
    },
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: filledInputRoot(palette, mode, densityKey, reducedMotion),
      },
    },
    MuiPickersFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: filledInputRoot(palette, mode, densityKey, reducedMotion),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.row,
          minHeight: d.controlHeight,
          transition: transitionFor(
            'feedback',
            ['border-color', 'box-shadow'],
            reducedMotion
          ),
          '&:has(:focus-visible)': focusRing(palette, mode),
          '@media (pointer: coarse)': { minHeight: target.coarseMin },
        },
        input: { paddingBlock: space.sm, paddingInline: space.md },
      },
    },
    // Without this the floating label falls back to MUI's hardcoded Material
    // easing; and the focused label stays a quiet secondary, since border
    // and ring already say "active" once.
    MuiInputLabel: {
      styleOverrides: {
        root: {
          transition: transitionFor(
            'feedback',
            ['color', 'transform'],
            reducedMotion
          ),
        },
        filled: {
          '&.Mui-focused': { color: palette.textSecondary.main },
          '&.Mui-error.Mui-focused': { color: palette.error.main },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { marginInline: 0, marginTop: space['2xs'] },
      },
    },
    MuiSelect: {
      variants: [
        {
          props: { variant: 'standard' as const, size: 'small' as const },
          style: {
            '&.MuiInputBase-root': {
              color: palette.gray.main,
              fontSize: sizing.selectSmall,
              fontWeight: 400,
            },
            '::before': {
              display: 'none',
            },
            '::after': {
              display: 'none',
            },
          },
        },
      ],
    },
    // Switch's `switchBase` is `styled(ButtonBase)`, so — like every other
    // ButtonBase-derived role — it takes `Mui-focusVisible`, not native
    // `:focus-visible`. Ring the track (the pill), not the small thumb:
    // this is MUI's own convention for its opt-in `theme.focusVisible`
    // engine on this exact component (`Switch.js`'s
    // `&.Mui-focusVisible ~ .MuiSwitch-track` rule).
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-focusVisible ~ .MuiSwitch-track': focusRing(palette, mode),
        },
      },
    },
  }
}
